import {
  load, save, getMe, saveMe, loadRoom, saveRoom, recentRooms, upsertMe, removeMember, addOther, markSent,
  activeMembers, snapshot, merge, randomId, setPhoto, tagMe, faceOwners, faceOf, ensureLink, cleanTable, stillToJoin,
} from './store.js'
import { normalize, pretty, cleanHandle, parseList } from './phone.js'
import { fallbackSmsFormats, firstMessage, smsLink, isApple } from './sms.js'
import {
  VCF_METHODS, buildVcf, deliverVcf, decodeRoster, linkUrl, linkPayload, readPayload, vcfFilename, isoDay,
  cardFilename, cardFiles, canShareCards,
} from './contacts.js'
import { newToken, isToken, get, getJson, putLink, putPhoto } from './vault.js'
import { readPhoto, loadImage, cropFace, faceBase64 } from './photo.js'
import { qrSvg } from './qr.js'
import { heroSvg } from './hero.js'
import { connect } from './room.js'

const app = document.getElementById('app')
const $ = (id) => document.getElementById(id)
let teardown = null
let onMeSaved = null
let helpHook = null // the room's "Didn't work?" sheet, opened when Messages didn't

// After a tap on a group-text link: if the page never loses focus, nothing opened (a laptop, a
// tablet without texting, a phone that doesn't take these links). Then offer the fallbacks
// instead of leaving the person looking at "You started the group text ✓".
function ifNothingOpened(fn) {
  let opened = false
  const mark = () => (opened = true)
  window.addEventListener('blur', mark)
  document.addEventListener('visibilitychange', mark)
  window.addEventListener('pagehide', mark)
  setTimeout(() => {
    window.removeEventListener('blur', mark)
    document.removeEventListener('visibilitychange', mark)
    window.removeEventListener('pagehide', mark)
    if (!opened && document.visibilityState === 'visible' && document.hasFocus()) fn()
  }, 2500)
}

// --- helpers ------------------------------------------------------------------

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])

const joinNames = (names) =>
  names.length < 3 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`

const plural = (n, one, many = one + 's') => `${n} ${n === 1 ? one : many}`

const showError = (text) => $('error') && ($('error').textContent = text || '')

const firstName = (name) => name.trim().split(/\s+/)[0]

// The line under a name: their number, and their Instagram if they gave it.
const detail = (m) => (m.ig ? `${pretty(m.phone)} · @${m.ig}` : pretty(m.phone))

// 2222's copy feedback: the button itself says "Copied" for a beat.
async function copyFrom(button, text) {
  try {
    await navigator.clipboard.writeText(text)
    const label = button.textContent
    button.textContent = 'Copied'
    setTimeout(() => (button.textContent = label), 1500)
  } catch {
    showError('Copy failed. Select the text and copy it by hand.')
  }
}

// --- "You" sheet (from "not you?" on home, or tapping your own row) ---------------------

function openMe() {
  $('meBody').innerHTML = profileForm({ cta: 'Save' })
  wireProfile($('meBody').querySelector('form'), (me) => {
    $('me').close()
    onMeSaved?.(me)
  })
  $('me').showModal()
}
document.querySelectorAll('[data-close]').forEach((b) => (b.onclick = () => $(b.dataset.close).close()))

// --- profile form: a name and a number, and an Instagram handle if they like ----------------------

let formSeq = 0
function profileForm({ cta, someoneElse = false, alt = '', name = '' }) {
  const me = someoneElse ? null : getMe()
  const id = `pf${++formSeq}`
  return `
    <form class="profile" novalidate>
      <label for="${id}-name">${someoneElse ? 'Name' : 'Your name'}</label>
      <input id="${id}-name" name="name" autocomplete="name" autocapitalize="words" required maxlength="60" value="${esc(me?.name ?? name)}" placeholder="First Last">
      <label for="${id}-phone">Mobile number</label>
      <input id="${id}-phone" name="phone" type="tel" inputmode="tel" autocomplete="tel" required value="${esc(me ? pretty(me.phone) : '')}" placeholder="+1 714 555 0123">
      <label for="${id}-ig">Instagram <span class="optional">(optional)</span></label>
      <input id="${id}-ig" name="ig" autocomplete="off" autocapitalize="none" autocorrect="off" spellcheck="false" maxlength="60" value="${esc(me?.ig ? `@${me.ig}` : '')}" placeholder="@handle">
      <button type="submit" class="suite-primary">${esc(cta)}</button>
      ${alt ? `<button type="submit" class="quiet alt" name="mode" value="alt">${esc(alt)}</button>` : ''}
      <p class="error" role="alert"></p>
    </form>`
}

// Returns {name, phone, ig} to onValid; onDone gets the saved profile, and whether the form's
// second button (`alt`) sent it.
function wireProfile(form, onDone, onValid = saveMe) {
  const field = (name) => form.elements.namedItem(name)
  const err = form.querySelector('.error')
  form.addEventListener('submit', (e) => {
    e.preventDefault()
    const name = field('name').value.trim()
    const phone = normalize(field('phone').value)
    if (!name) {
      err.textContent = 'Add a name so people know who this is.'
      return field('name').focus()
    }
    if (!phone) {
      err.textContent = "That doesn't look like a phone number. Include the country code if you're not local (+44…)."
      return field('phone').focus()
    }
    const ig = cleanHandle(field('ig').value)
    if (ig === false) {
      err.textContent = "That doesn't look like an Instagram handle. Leave it empty if you'd rather."
      return field('ig').focus()
    }
    err.textContent = ''
    const result = onValid({ name, phone, ...(ig ? { ig } : {}) })
    if (typeof result === 'string') err.textContent = result
    else onDone(result, e.submitter?.value === 'alt')
  })
}

// The browser's own contact picker (Chrome on Android; Safari only behind a flag).
const canPickContacts = 'contacts' in navigator && typeof navigator.contacts?.select === 'function'

// --- picking the group photo ------------------------------------------------------------------
// Usually it's already been taken, so this opens the photo library (with the camera as an option),
// not the camera. One hidden file input (in index.html); it has to be clicked during a tap, so Start
// opens it right away, and the photo comes back after the room is already up.

let onPhoto = null
let waitingPhoto = null
$('photoPick').addEventListener('change', (e) => {
  const file = e.target.files?.[0]
  e.target.value = ''
  if (!file) return
  if (onPhoto) onPhoto(file)
  else waitingPhoto = file
})
const pickPhoto = () => $('photoPick').click()

// --- the group photo with tappable faces ------------------------------------------------------

// Draws the photo with a ring on each face. paint(i => ({cls, tag, label})) restyles the rings in
// place, so names can stream in without the image reloading.
function facePhoto(box, { url, w, h, faces }) {
  box.innerHTML = `<figure class="photo"><img alt="Group photo" src="${esc(url)}">${faces
    .map((_, i) => `<button type="button" class="face" data-face="${i}"><span class="tag"></span></button>`)
    .join('')}</figure>`
  const fig = box.firstElementChild
  fig.style.aspectRatio = `${w} / ${h}`
  fig.faces = faces
  const rings = [...fig.querySelectorAll('.face')]
  // Positioned through the CSSOM: the CSP allows no inline style attributes.
  rings.forEach((b, i) => {
    const [x, y, fw, fh] = faces[i]
    Object.assign(b.style, {
      left: `${(x + fw / 2) * 100}%`, top: `${(y + fh / 2) * 100}%`, width: `${fw * 118}%`, height: `${fh * 112}%`,
    })
  })
  return {
    paint(fn) {
      rings.forEach((b, i) => {
        const { cls = '', tag = '', label = 'Face' } = fn(i)
        const className = `face ${cls}`.trim()
        if (b.className !== className) b.className = className
        const t = b.firstElementChild
        if (t.textContent !== tag) t.textContent = tag
        if (b.getAttribute('aria-label') !== label) b.setAttribute('aria-label', label)
      })
    },
  }
}

// Which face a tap meant: the one whose center is nearest, if the tap is close to it. Faces in a
// group photo are small on a phone and their rings overlap, so this beats the rings' own edges.
// A keyboard press on a ring means that ring.
function faceTapped(e) {
  const fig = e.target.closest('.photo')
  const ring = e.target.closest('.face')
  if (!fig?.faces) return null
  if (e.detail === 0) return ring ? Number(ring.dataset.face) : null
  const r = fig.getBoundingClientRect()
  let best = null, bestD = Infinity
  fig.faces.forEach(([x, y, w, h], i) => {
    const dx = (x + w / 2) * r.width - (e.clientX - r.left), dy = (y + h / 2) * r.height - (e.clientY - r.top)
    const d = Math.hypot(dx, dy)
    if (d < bestD && d <= Math.max(30, Math.max(w * r.width, h * r.height) * 0.9)) [best, bestD] = [i, d]
  })
  return best
}

// Face crops, as small data: URLs for list avatars. Cached per photo.
const thumbs = new Map()
function faceThumb(photo, i) {
  const key = `${photo.t}:${i}`
  if (!thumbs.has(key)) thumbs.set(key, cropFace(photo.img, photo.faces[i], 96).toDataURL('image/jpeg', 0.8))
  return thumbs.get(key)
}
const avatar = (photo, face) =>
  photo?.img && face != null ? `<img class="avatar" alt="" src="${faceThumb(photo, face)}">` : photo?.img ? '<span class="avatar none" aria-hidden="true"></span>' : ''

// Fetches and decrypts a group photo. Returns {t, w, h, faces, img, url}.
const photoCache = new Map()
function fetchPhoto(meta) {
  if (!photoCache.has(meta.t)) {
    const p = (async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          const bytes = await get('photo', meta.t)
          if (!bytes) return null
          const { img, url } = await loadImage(new Blob([bytes], { type: 'image/jpeg' }))
          return { ...meta, img, url }
        } catch (e) {
          if (attempt >= 3) throw e
          await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt))
        }
      }
    })()
    p.catch(() => photoCache.delete(meta.t))
    photoCache.set(meta.t, p)
  }
  return photoCache.get(meta.t)
}

// Each person with their face, ready for a contact card.
async function withPhotos(members, photo) {
  return Promise.all(members.map(async (m) =>
    photo?.img && m.face != null ? { ...m, photo: await faceBase64(photo.img, photo.faces[m.face]) } : m))
}

// --- home: one tap (or one form, the first time) to a live QR, then the group photo -----------------

let starting = false
function newRoom() {
  if (starting) return // a double tap shouldn't make two groups
  starting = true
  setTimeout(() => (starting = false), 1500)
  const room = loadRoom(randomId(10), randomId(12))
  saveRoom(room)
  location.hash = `g=${room.id}.${room.secret}`
  pickPhoto()
}

function showHome() {
  onMeSaved = showHome
  const me = getMe()
  const recent = recentRooms()
  app.innerHTML = `
    <section class="login">
      <h1>Start the group text</h1>
      <p class="muted">Add a group photo, everyone scans one code, and one tap texts them all.</p>
      ${heroSvg()}
      ${me
        ? `<button type="button" class="block suite-primary" id="start">Start a group text</button>
           <button type="button" class="block quiet alt" id="paste">Paste numbers instead</button>
           <p class="fine as">As ${esc(me.name)} · <button type="button" class="linkbtn" id="editMe">not you?</button></p>`
        : profileForm({ cta: 'Start a group text', alt: 'Paste numbers instead' })}
      <p class="fine privacy">Only the group can see the names, numbers and photo. <a href="/privacy">Privacy</a></p>
    </section>
    ${recent.length ? `
      <div class="list-label"><b>Recent</b></div>
      <div class="list">
        ${recent.slice(0, 3).map((r) => `
          <a class="card tap" href="#g=${esc(r.id)}.${esc(r.secret)}">
            <div class="who"><p class="where">${esc(joinNames(r.names ?? []) || 'Just you')}</p>
              <p class="what">${new Date(r.t).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</p></div>
            <span class="chev" aria-hidden="true">›</span>
          </a>`).join('')}
      </div>` : ''}`

  if (me) {
    $('start').onclick = newRoom
    $('paste').onclick = () => (location.hash = 'paste')
    $('editMe').onclick = openMe
  } else {
    wireProfile(app.querySelector('form'), (_, paste) => (paste ? (location.hash = 'paste') : newRoom()))
  }
}

// --- pasting numbers ----------------------------------------------------------------------------
// For when scanning won't work: people who aren't here, or phones that can't open the page. One
// box takes whatever gets pasted or typed (parseList reads names and numbers out of it), and the
// list below shows what it read as it goes. Rows without a name get a field for one, since a
// contact that's only a number is no help later.

const canReadClipboard = typeof navigator.clipboard?.readText === 'function'

function pasteBox(root, { myPhone, existing = new Set(), onChange }) {
  const names = new Map() // phone -> a name typed in the list
  let read = { people: [], unread: [], me: false }
  // Android's contact picker is quicker than copying numbers from somewhere; picks go into the box.
  root.innerHTML = `
    <div class="paste-btns">
      ${canReadClipboard ? '<button type="button" class="quiet" data-act="clip">Paste</button>' : ''}
      ${canPickContacts ? '<button type="button" class="quiet" data-act="contacts">Choose from contacts</button>' : ''}
    </div>
    ${canReadClipboard ? '' : '<p class="fine paste-hint">Long-press the box to paste.</p>'}
    <textarea class="paste-text" rows="4" aria-label="Names and numbers" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false"
      placeholder="Sam Lee  415 555 0102&#10;Ana  (415) 555-0103"></textarea>
    <p class="error paste-error" role="alert"></p>
    <div class="list paste-list"></div>
    <p class="error paste-unread"></p>
    <p class="fine paste-note"></p>`
  const box = root.querySelector('.paste-text')
  const err = (t) => (root.querySelector('.paste-error').textContent = t)
  const people = () => read.people.filter((p) => !existing.has(p.phone)).map((p) => ({ ...p, name: (names.get(p.phone) ?? p.name).trim() }))

  function render() {
    read = parseList(box.value, myPhone)
    root.querySelector('.paste-list').innerHTML = read.people.map((p) => {
      const typed = names.get(p.phone)
      const name = p.name
        ? `<p class="where">${esc(p.name)}</p>`
        : `<input class="name-in" data-phone="${esc(p.phone)}" value="${esc(typed ?? '')}" placeholder="Add a name" aria-label="Name for ${esc(pretty(p.phone))}" autocapitalize="words" autocomplete="off" maxlength="60">`
      return `<div class="card paste-row${existing.has(p.phone) ? ' dup' : ''}"><div class="who">${name}<p class="what">${esc(pretty(p.phone))}</p></div>${
        existing.has(p.phone) ? '<span class="chip">already in</span>' : ''}</div>`
    }).join('')
    root.querySelector('.paste-unread').textContent = read.unread.length
      ? `Couldn't read ${read.unread.map((u) => `“${u}”`).join(', ')}. Numbers from abroad need their country code (+44…).`
      : ''
    const notes = []
    if (read.me) notes.push("Your own number is left out, since you're sending it.")
    if (read.people.length > 32) notes.push('Group iMessages hold up to 32 people.')
    root.querySelector('.paste-note').textContent = notes.join(' ')
    err('')
    onChange(people())
  }

  box.addEventListener('input', render)
  root.addEventListener('input', (e) => {
    if (!e.target.matches('.name-in')) return
    names.set(e.target.dataset.phone, e.target.value)
    onChange(people())
  })
  const append = (text) => {
    box.value = box.value.trim() ? `${box.value.trim()}\n${text}` : text
    render()
  }
  // One tap instead of long-press → Paste. Safari asks with its own small Paste button.
  root.querySelector('[data-act=clip]')?.addEventListener('click', async () => {
    try {
      const text = (await navigator.clipboard.readText()).trim()
      if (!text) return err('Nothing to paste. Copy the numbers first.')
      append(text)
    } catch {
      box.focus()
      err("Couldn't paste. Long-press the box and tap Paste.")
    }
  })
  root.querySelector('[data-act=contacts]')?.addEventListener('click', async () => {
    try {
      const picks = await navigator.contacts.select(['name', 'tel'], { multiple: true })
      const lines = picks.flatMap((c) => {
        const tel = (c.tel ?? []).find((t) => normalize(t))
        return tel ? [`${c.name?.find((n) => n?.trim())?.trim() ?? ''} ${tel}`.trim()] : []
      })
      if (lines.length) append(lines.join('\n'))
      if (lines.length < picks.length) err(`${plural(picks.length - lines.length, 'contact')} had no usable mobile number.`)
    } catch (e) {
      if (e.name !== 'AbortError') err("Couldn't open contacts. Paste or type the numbers instead.")
    }
  })
  render()
  return { focus: () => box.focus() }
}

// Start a group text from a pasted list: no scanning. Tapping the button turns it into a group
// (you plus everyone pasted) that's already been texted, so you land on the room's "sent" screen
// with Save contacts, Send cards and Didn't work?.
function showPaste() {
  const me = getMe()
  if (!me) return void (location.hash = '')
  const room = loadRoom(randomId(10), randomId(12))
  ensureLink(room, newToken())
  let list = []
  app.innerHTML = `
    <section class="login paste">
      <h1>Paste their numbers</h1>
      <p class="muted">Names too, if you have them. Everyone gets the group text and each other's contacts.</p>
      <div id="pasteBox"></div>
    </section>
    <div class="actionbar"><div class="bar-inner" id="pasteBar"></div></div>`
  document.body.classList.add('with-bar')

  const members = () => [{ name: me.name, phone: me.phone, ...(me.ig ? { ig: me.ig } : {}) },
    ...list.map((p) => ({ name: p.name || pretty(p.phone), phone: p.phone }))]
  const body = () => firstMessage(linkUrl(room.link.t), firstName(me.name))
  // The link's list is saved as it changes, so it works the moment the text arrives.
  let timer = null
  const saveLink = (keepalive) => putLink(room.link.t, linkPayload(room, members(), () => null), { keepalive }).catch(() => {})

  pasteBox($('pasteBox'), {
    myPhone: me.phone,
    onChange(people) {
      list = people
      const n = list.length
      $('pasteBar').innerHTML = n
        ? `<a class="button primary" data-sms href="${esc(smsLink(list.map((p) => p.phone), body()))}">Start group text (${n})</a>`
        : '<button type="button" class="primary" disabled>Start group text</button>'
      clearTimeout(timer)
      if (n) timer = setTimeout(() => saveLink(false), 800)
    },
  })

  app.onclick = (e) => {
    if (!e.target.closest('[data-sms]')) return
    upsertMe(room, me)
    const now = Date.now()
    list.forEach((p, i) => {
      const m = addOther(room, { name: p.name || pretty(p.phone), phone: p.phone }, me.id)
      if (m) m.j = m.u = now + i + 1 // keep the pasted order
    })
    room.remote = true
    markSent(room, me.id)
    saveRoom(room)
    clearTimeout(timer)
    saveLink(true)
    ifNothingOpened(() => helpHook?.(true))
    // Messages opens from the link itself; the room loads behind it.
    setTimeout(() => (location.hash = `g=${room.id}.${room.secret}`), 60)
  }
  teardown = () => {
    clearTimeout(timer)
    document.body.classList.remove('with-bar')
  }
}

// --- room: guests connect while they type; the QR stays up while the list fills in --------------

// Keep the screen on while the QR is up so it doesn't dim mid-scan. Browsers drop the lock when
// the page is hidden, so it's re-requested on return and on taps.
let wakeLock = null
async function keepAwake() {
  if (wakeLock || document.visibilityState !== 'visible' || !navigator.wakeLock) return
  try {
    wakeLock = await navigator.wakeLock.request('screen')
    wakeLock.addEventListener('release', () => (wakeLock = null))
  } catch {}
}

const summarize = (names) =>
  names.length <= 4 ? joinNames(names) : `${names.slice(0, 3).join(', ')} and ${names.length - 3} others`

// `from` is what a link from split carries: your name, and the table's names.
function showRoom(code, from = {}) {
  const [id, secret] = code.split('.')
  if (!/^[\w-]{6,64}$/.test(id) || !/^[\w-]{6,64}$/.test(secret ?? '')) return showHome()

  const room = loadRoom(id, secret)
  if (from.table?.length) room.table = cleanTable(from.table)
  // Everyone at a bill arrives by link, so nobody is holding up a code: it's there for anyone else.
  const fromTable = !!room.table?.length
  let me = getMe()
  let joined = false
  const joinUrl = `${location.origin}/#g=${id}.${secret}`
  const ui = { status: null, editing: null, fresh: new Set(), live: new Map(), pickedFace: null }
  // The photo as this phone has it: null, {busy, url} while it's being read, or the fetched photo.
  let pic = null
  let picError = ''
  let loadingT = null // the photo being (or last) fetched, so a failure doesn't retry on every render

  const people = () => activeMembers(room)
  const others = () => people().filter((m) => m.phone !== me?.phone)
  const nameOf = (memberId) => (memberId === me?.id ? 'You' : firstName(room.members[memberId]?.name ?? 'Someone'))
  const addedBy = (m) => (m.by === me?.id ? 'added by you' : room.members[m.by] ? `added by ${nameOf(m.by)}` : 'added')
  const withFaces = (list) => {
    const owners = faceOwners(room)
    return list.map((m) => ({ ...m, face: faceOf(room, m, owners) }))
  }
  const link = () => room.link && linkUrl(room.link.t)
  const body = () => firstMessage(link(), me && firstName(me.name))
  const day = () => isoDay(room.created)
  const sms = () => smsLink(others().map((m) => m.phone), body())
  const ready = () => (pic?.img && pic.t === room.photo?.t ? pic : null)

  // Before joining: who's already here, the photo to tap yourself in, and the form. The
  // connection is already up while they type, so joining is instant.
  function mountJoin() {
    viewKey = ''
    app.innerHTML = `
      <section class="login">
        <h1 id="joinTitle">Join the group text</h1>
        <p class="muted" id="joinWho"></p>
        <div id="photoBox"></div>
        ${profileForm({ cta: 'Join', name: from.name ?? '' })}
        <p class="fine privacy">Only shared with this group. <a href="/privacy">Privacy</a></p>
      </section>`
    const form = app.querySelector('form')
    wireProfile(form, (saved) => {
      me = saved
      enter()
    })
    // With the name already filled in from the bill, the number is all that's left.
    if (!room.photo) form.elements.namedItem(from.name ? 'phone' : 'name').focus()
    liveForm(form, 'joining', null, () => ui.pickedFace)
    renderJoin()
  }

  function renderJoin() {
    const list = people()
    $('joinTitle').textContent = list.length ? `Join ${firstName(list[0].name)}'s group text` : 'Join the group text'
    const typing = ghosts().filter((g) => g.joining).map((g) => (g.name.trim() ? firstName(g.name) : 'someone'))
    const also = joinNames(typing)
    $('joinWho').textContent = (list.length
      ? `${summarize(list.map((m) => firstName(m.name)))} ${list.length === 1 ? 'is' : 'are'} in.`
      : 'Everyone gets your number and you get theirs.') +
      (also ? ` ${also[0].toUpperCase()}${also.slice(1)} ${typing.length === 1 ? 'is' : 'are'} joining too.` : '')
    renderPhoto()
  }

  function enter() {
    joined = true
    present({ joining: undefined })
    upsertMe(room, me)
    if (ui.pickedFace != null && room.photo) tagMe(room, me.id, ui.pickedFace)
    saveRoom(room)
    conn?.broadcast()
    mountRoom()
    keepAwake()
  }

  // A group started from pasted numbers (`remote`, this phone only) has nobody to scan, so its
  // code is tucked away and the photo isn't asked for.
  const remote = !!room.remote
  function mountRoom() {
    viewKey = '' // a fresh #photoBox
    app.innerHTML = `
      ${remote
        ? '<section class="remote-head"><h1 id="scanTitle"></h1><p id="sync" class="tiny"></p></section>'
        : `<section class="scan">
        <h1 id="scanTitle"></h1>
        <div class="qr">${qrSvg(joinUrl)}</div>
        <p id="sync" class="tiny"></p>
      </section>`}
      <div id="photoBox"></div>
      <div id="people" class="list"></div>
      <div id="ghosts" class="ghosts"></div>
      <p id="still" class="still"></p>
      <button type="button" class="addrow hidden" data-act="photo" id="photoRow"><span class="avatar ghost" aria-hidden="true">+</span>Add the group photo</button>
      <button type="button" class="addrow" data-act="add"><span class="avatar ghost" aria-hidden="true">+</span>${remote ? 'Add more people' : "Add someone who isn't here"}</button>
      ${remote ? `<details class="joincode"><summary>Someone here? Show a code they can scan</summary><div class="qr">${qrSvg(joinUrl)}</div></details>` : ''}
      <p id="error" class="error" role="alert"></p>
      <div id="bar" class="actionbar hidden">
        <p class="bar-note" id="barNote"></p>
        <div class="bar-inner" id="barInner"></div>
        <p class="fine" id="barFine"></p>
      </div>`
    render()
  }

  // The host holds the code up; a guest first needs to know they made it in. Their phone shows the
  // code too, so whoever's closest can be scanned.
  const isHost = () => people()[0]?.id === me.id

  // Quiet when things work (the list filling up is the signal); only speaks up while connecting.
  function renderStatus() {
    const s = ui.status
    const up = s?.relay === 'open' || Object.keys(s?.peers ?? {}).length
    if (remote) {
      $('scanTitle').textContent = 'Your group text'
      return
    }
    const holding = isHost() && !fromTable
    $('scanTitle').textContent = holding ? 'Scan to join' : "You're in ✓"
    $('sync').className = `tiny${s?.error ? ' bad' : ''}`
    $('sync').innerHTML = s?.error ? esc(s.error) : up ? (holding ? '' : 'Others can scan this too.') : '<span class="spinner"></span>connecting…'
  }

  // --- the photo --------------------------------------------------------------------------
  // Whoever starts the group adds it. The server finds the faces and keeps it encrypted, and
  // everyone else's phone fetches it. Each person taps their own face.

  let view = null
  let viewKey = ''
  function renderPhoto() {
    const box = $('photoBox')
    if (!box) return
    const p = ready()
    if (room.photo && !p && !pic?.busy && loadingT !== room.photo.t) loadPhoto()
    const key = pic?.busy ? `busy:${pic.url}` : p ? `photo:${p.t}` : picError ? `err:${picError}` : room.photo ? `wait:${room.photo.t}` : 'none'
    if (key !== viewKey) {
      viewKey = key
      view = null
      if (pic?.busy) {
        box.innerHTML = `<figure class="photo busy"><img alt="" src="${esc(pic.url)}"><figcaption><span class="spinner"></span>${esc(pic.busy)}</figcaption></figure>`
      } else if (p) {
        view = facePhoto(box, p)
        box.insertAdjacentHTML('beforeend', '<p class="photo-note" id="photoNote"></p>')
      } else if (picError) {
        const retry = lastShot ? ' <button type="button" class="linkbtn" data-act="resend-photo">Try again</button>'
          : room.photo ? ' <button type="button" class="linkbtn" data-act="retry-photo">Try again</button>' : ''
        box.innerHTML = `<p class="error photo-error">${esc(picError)}${retry}</p>`
      } else if (room.photo) {
        box.innerHTML = '<figure class="photo waiting"><figcaption><span class="spinner"></span>Loading the photo…</figcaption></figure>'
        box.firstElementChild.style.aspectRatio = `${room.photo.w} / ${room.photo.h}`
      } else {
        box.innerHTML = ''
      }
    }
    $('photoRow')?.classList.toggle('hidden', !!room.photo || !!pic?.busy || remote)
    if (view) paintPhoto(p)
  }

  // Names on faces: yours, everyone's tags, and live, the face a guest picked while joining.
  function paintPhoto(p) {
    const owners = faceOwners(room)
    const picking = new Map()
    for (const g of ghosts()) if (g.joining && g.face != null) picking.set(g.face, g.name.trim() ? firstName(g.name) : 'Someone')
    const mineIndex = joined ? faceOf(room, room.members[me.id], owners) : ui.pickedFace
    view.paint((i) => {
      const owner = owners.get(i)
      if (i === mineIndex) return { cls: 'mine', tag: 'You', label: 'You. Tap to untag' }
      if (owner) return { cls: 'taken', tag: firstName(owner.name), label: `${owner.name}. Tap if this is you` }
      if (picking.has(i)) return { cls: 'live', tag: `${picking.get(i)}…`, label: 'Being tagged' }
      return { cls: 'open', label: 'Tap if this is you' }
    })
    const note = $('photoNote')
    if (!note) return
    const tagged = mineIndex != null
    note.innerHTML = !p.faces.length
      ? `No faces found.${room.photo.by === me?.id ? ' <button type="button" class="linkbtn" data-act="photo">Pick another</button>' : ''}`
      : !tagged && (!joined || room.photo.by !== me.id)
        ? '<b>Tap your face</b> so everyone knows who you are.'
        : room.photo.by === me?.id
          ? `Everyone taps their own face. <button type="button" class="linkbtn" data-act="photo">Change photo</button>`
          : ''
  }

  async function loadPhoto() {
    const meta = room.photo
    loadingT = meta.t
    picError = ''
    try {
      const p = await fetchPhoto(meta)
      if (room.photo?.t !== meta.t) return
      if (!p) {
        picError = 'The photo has expired.'
        return render()
      }
      pic = p
      picError = ''
      render()
    } catch {
      if (room.photo?.t === meta.t) {
        picError = "Couldn't load the photo."
        render()
      }
    }
  }

  // The photo goes up once; the server finds the faces and keeps an encrypted copy. On a weak
  // signal it tries a few times, then offers to try again with the same shot.
  let lastShot = null
  async function addPhoto(file) {
    showError('')
    picError = ''
    let shot
    try {
      shot = file ? await readPhoto(file) : lastShot
    } catch (e) {
      picError = `${e.message}. Try another one.`
      return render()
    }
    lastShot = shot
    const preview = URL.createObjectURL(shot.jpeg)
    pic = { busy: 'Finding faces…', url: preview }
    render()
    try {
      const t = newToken()
      const bytes = new Uint8Array(await shot.jpeg.arrayBuffer())
      let found
      for (let attempt = 0; ; attempt++) {
        try {
          found = await putPhoto(t, bytes)
          break
        } catch (e) {
          if (attempt >= 2 || /didn't work/.test(e.message)) throw e
          await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)))
        }
      }
      const { img, url } = await loadImage(shot.jpeg)
      const photo = { t, w: shot.w, h: shot.h, faces: found.faces }
      photoCache.set(t, Promise.resolve({ ...photo, img, url }))
      setPhoto(room, photo, me.id)
      pic = { ...photo, img, url }
      lastShot = null
      commit()
    } catch (e) {
      pic = null
      picError = /didn't work/.test(e.message) ? `${e.message}. Try another one.` : "Couldn't send the photo. Check your signal."
      render()
    } finally {
      URL.revokeObjectURL(preview)
    }
  }

  function tapFace(i) {
    if (!joined) {
      ui.pickedFace = ui.pickedFace === i ? null : i
      present({}) // liveForm sends the picked face with the name
      renderPhoto()
      return app.querySelector('form.profile input[name=name]')?.focus()
    }
    if (tagMe(room, me.id, i)) commit()
  }

  function renderPeople() {
    const list = withFaces(people())
    const p = ready()
    $('people').innerHTML =
      list
        .map((m) => {
          const isMe = m.phone === me.phone
          return `
          <div class="card tap${ui.fresh.has(m.id) ? ' fresh' : ''}" data-id="${esc(m.id)}" role="button" tabindex="0">
            ${avatar(p, m.face)}
            <div class="who"><p class="where">${esc(m.name)}</p><p class="what">${esc(detail(m))}</p></div>
            <span class="chip livechip"></span>
            ${isMe ? '<span class="chip">you</span>' : m.by && !(remote && m.by === me.id) ? `<span class="chip">${esc(addedBy(m))}</span>` : ''}
            ${ui.editing === m.id && !isMe ? `<div class="row-actions">
              <button type="button" class="linkbtn danger" data-act="remove">Remove</button>
              <button type="button" class="linkbtn" data-act="unedit">Keep</button></div>` : ''}
          </div>`
        })
        .join('') + (list.length < 2 && !remote && !fromTable ? '<p class="empty">Waiting for the first scan…</p>' : '')
    const missing = fromTable ? stillToJoin(room.table, list) : []
    $('still').textContent = missing.length ? `Still to join: ${joinNames(missing.map(firstName))}` : ''
  }

  // The bar slides in once there's someone to text and stays pinned so the QR never moves.
  // Once someone starts the text, everyone sees it, so the table doesn't end up with three threads.
  // Anyone who joins after that gets a fresh thread, since mixed groups can't add people later.
  function renderBar() {
    const n = others().length
    $('bar').classList.toggle('hidden', !n)
    document.body.classList.toggle('with-bar', n > 0)
    if (!n) return
    if (ensureLink(room, newToken())) {
      saveRoom(room)
      conn?.broadcast()
    }
    const sent = room.sent
    const missed = sent ? people().filter((m) => !sent.phones.includes(m.phone)) : []
    const saveBtn = (cls) => `<button type="button" class="${cls}" data-act="save-contacts">Save ${plural(n, 'contact')}</button>`
    const text = (cls, label) => `<a class="button ${cls}" data-sms href="${esc(sms())}">${label}</a>`
    const help = `<button type="button" class="linkbtn" data-act="help">Didn't work?</button>`

    if (!sent) {
      $('barNote').textContent = ''
      $('barInner').innerHTML = text('primary', `Start group text (${n})`) + saveBtn('quiet')
      $('barFine').innerHTML = `Only one person needs to tap this · ${help}`
    } else if (missed.length) {
      $('barNote').textContent = `${nameOf(sent.by)} started the group text. ${summarize(missed.map((m) => firstName(m.name)))} ${missed.length === 1 ? "isn't" : "aren't"} in it.`
      $('barInner').innerHTML = text('primary', `New group text (${n})`) + saveBtn('quiet')
      $('barFine').innerHTML = `Group texts can't add people later, so this starts a new one · ${help}`
    } else if (sent.by === me.id) {
      // The sender can also drop everyone's contact card into the thread, one card per person.
      $('barNote').textContent = 'You started the group text ✓'
      $('barInner').innerHTML = saveBtn('primary') + (canShareCards() ? '<button type="button" class="quiet" data-act="send-cards">Send cards</button>' : '')
      $('barFine').innerHTML = `<a data-sms href="${esc(sms())}">Open it again</a> · ${help}`
      if (canShareCards()) prepareCards()
    } else {
      $('barNote').textContent = `${nameOf(sent.by)} started the group text ✓ Check Messages.`
      $('barInner').innerHTML = saveBtn('primary')
      $('barFine').innerHTML = `Didn't get it? <a data-sms href="${esc(sms())}">Start it yourself</a> · ${help}`
    }
    if ($('help').open) renderHelp()
    if ($('save').open) saveSheet?.refresh(saveList())
    syncLink()
  }

  // --- the link in the text -------------------------------------------------------------------
  // /c#<token> opens everyone's names, numbers and faces. The contents are kept encrypted on the
  // server and updated as people join, so the link is short and the same for the whole night.
  // The host keeps it current; whoever taps "Start group text" sends the latest right then.
  let linkSaved = ''
  let linkTimer = null
  function syncLink(now = false) {
    if (!room.link || !others().length || (!now && !isHost() && room.sent?.by !== me.id)) return
    const data = linkPayload(room, people(), (m) => faceOf(room, m))
    const json = JSON.stringify(data)
    if (json === linkSaved) return
    clearTimeout(linkTimer)
    const go = () =>
      putLink(room.link.t, data, { keepalive: now })
        .then(() => (linkSaved = json))
        .catch(() => !left && (linkTimer = setTimeout(() => syncLink(), 5000)))
    if (now) go()
    else linkTimer = setTimeout(go, 1200)
  }

  // Everyone's card, built ahead of time: Safari only opens the share sheet straight from a tap.
  let cards = null
  let cardsKey = ''
  function prepareCards() {
    const list = withFaces(people())
    const key = JSON.stringify([list.map((m) => [m.name, m.phone, m.face]), ready()?.t])
    if (key === cardsKey) return
    cardsKey = key
    cards = null
    withPhotos(list, ready()).then((full) => {
      if (cardsKey === key) cards = cardFiles(full, { day: day() })
    })
  }

  async function sendCards(el) {
    if (!cards) return showError('Getting the cards ready. Tap again in a second.')
    try {
      await navigator.share({ files: cards })
    } catch (e) {
      if (e.name !== 'AbortError') showError("Couldn't open the share sheet. Save contacts instead.")
    }
  }

  // "Didn't work?": the fallbacks, in order of effort. Other link shapes for this phone's Messages
  // app, then copy-and-paste (always works), then the other ways to save contacts. The tiny
  // format names are there so a failed attempt can be reported precisely.
  function renderHelp(didntOpen = false) {
    const nums = others().map((m) => m.phone)
    const s = ui.status
    const otherWays = `
      <h3>Open Messages another way</h3>
      <div class="help-list">
        ${fallbackSmsFormats.map((k, i) => `
          <a class="button quiet" data-sms href="${esc(smsLink(nums, body(), k))}">Try way ${i + 2}<small>${esc(k)}</small></a>`).join('')}
      </div>`
    const pasteIt = `
      <h3>${didntOpen ? 'Paste it yourself' : 'Or paste it yourself'}</h3>
      <p class="fine">Copy the numbers, start a new message, and paste them into “To”. Then copy the message and paste it in.</p>
      <div class="help-list">
        <button type="button" class="quiet" data-act="copy-numbers">1. Copy ${plural(nums.length, 'number')}</button>
        <button type="button" class="quiet" data-act="copy-message">2. Copy the message</button>
      </div>
      <p class="fine after">If the numbers don't split into separate people, add them one at a time from the list.</p>`
    // When nothing opened, copying is the way that works, so it comes first.
    $('helpBody').innerHTML = `
      ${didntOpen ? `<p class="help-note">Messages didn't open here. Copy the numbers and the message into a new message, or try another way.</p>${pasteIt}${otherWays.replace('Open Messages another way', 'Or try another way')}` : `${otherWays}${pasteIt}`}
      <h3>Contacts didn't save?</h3>
      <p class="fine">These save everyone at once.</p>
      <div class="help-list">
        ${Object.entries(VCF_METHODS).map(([k, label]) =>
          `<button type="button" class="quiet" data-act="vcf" data-method="${k}">${esc(label)}<small>${esc(k)}</small></button>`).join('')}
      </div>
      <details class="conn"><summary>connection info</summary><pre class="debug">${esc(JSON.stringify({
        relay: s?.relay, turn: s?.turn, peers: s ? Object.keys(s.peers).length : 0, error: s?.error,
        wakeLock: !!wakeLock, contactPicker: canPickContacts, shareCards: canShareCards(), ua: navigator.userAgent,
      }, null, 2))}</pre></details>`
  }

  function render() {
    if (!joined) return renderJoin()
    renderStatus()
    renderPhoto()
    renderPeople()
    renderLive()
    renderBar()
  }

  // --- live presence --------------------------------------------------------------------------
  // What this phone is doing right now goes to everyone (room.js); what everyone else is doing
  // shows up as it happens: people still typing as ghost rows, names changing letter by letter
  // with a caret, rows lit up while someone has them open. Numbers being typed show only as dots,
  // since they aren't shared until the button is tapped.
  let mine = {}
  function present(patch) {
    mine = { ...mine, ...patch }
    for (const k of Object.keys(mine)) if (mine[k] === undefined) delete mine[k]
    conn?.setPresence({ ...mine, from: me?.id })
  }

  let refreshForm = null
  function liveForm(form, key, dialog, face = () => null) {
    const field = (n) => form.elements.namedItem(n)
    let done = false
    const update = () => {
      // Closing or submitting blurs the input, and that late update mustn't bring the ghost back.
      if (done || !form.isConnected) return
      const f = document.activeElement?.form === form ? document.activeElement.name : null
      const picked = face()
      present({ [key]: {
        name: field('name').value,
        digits: field('phone').value.replace(/\D/g, '').length,
        field: f === 'name' || f === 'phone' ? f : null,
        ...(picked != null ? { face: picked } : {}),
      } })
    }
    form.addEventListener('input', update)
    form.addEventListener('focusin', update)
    form.addEventListener('focusout', () => setTimeout(update)) // focus has moved by the next tick
    update()
    if (key === 'joining') refreshForm = update
    dialog?.addEventListener('close', () => {
      done = true
      present({ [key]: undefined })
    }, { once: true })
  }

  const actor = (p) => (p.from && room.members[p.from] ? firstName(room.members[p.from].name) : 'Someone')

  // People mid-form: guests who scanned but haven't joined yet, and people being added.
  function ghosts() {
    const out = []
    for (const [peer, p] of ui.live) {
      if (p.joining && !(p.from && room.members[p.from])) out.push({ key: `j${peer}`, joining: true, ...p.joining, chip: 'joining…' })
      if (p.adding) out.push({ key: `a${peer}`, ...p.adding, chip: `${actor(p)} is adding` })
    }
    return out
  }

  const CARET = '<span class="caret" aria-hidden="true"></span>'
  const typed = (g) => ({
    name: g.name.trim() || g.field === 'name' ? `${esc(g.name)}${g.field === 'name' ? CARET : ''}` : '<span class="someone">Someone</span>',
    phone: g.digits || g.field === 'phone' ? `<span class="dots">${'•'.repeat(g.digits)}</span>${g.field === 'phone' ? CARET : ''}` : '&nbsp;',
  })

  // Patch in place (no full re-render) so carets and highlights don't flicker at typing speed.
  const patch = (el, html) => {
    if (el && el.gcHtml !== html) {
      el.innerHTML = html
      el.gcHtml = html
    }
  }

  function renderLive() {
    if (!joined) return renderJoin()
    const box = $('ghosts')
    const list = ghosts()
    const keys = new Set(list.map((g) => g.key))
    for (const el of [...box.children]) if (!keys.has(el.dataset.ghost)) el.remove()
    for (const g of list) {
      let el = box.querySelector(`[data-ghost="${CSS.escape(g.key)}"]`)
      if (!el) {
        if (g.joining) navigator.vibrate?.(15) // a scan just landed (Android; iOS has no vibrate)
        el = document.createElement('div')
        el.className = 'card ghost'
        el.dataset.ghost = g.key
        el.innerHTML = '<div class="who"><p class="where"></p><p class="what"></p></div><span class="chip"></span>'
        box.append(el)
      }
      const t = typed(g)
      patch(el.querySelector('.where'), t.name)
      patch(el.querySelector('.what'), t.phone)
      patch(el.querySelector('.chip'), esc(g.chip))
    }
    $('people').querySelector('.empty')?.classList.toggle('hidden', list.length > 0)

    const editing = new Map()
    const touching = new Map()
    for (const p of ui.live.values()) {
      if (p.editing && p.from) editing.set(p.from, p.editing)
      if (p.touching) touching.set(p.touching, actor(p))
    }
    for (const row of $('people').querySelectorAll('.card[data-id]')) {
      const m = room.members[row.dataset.id]
      const e = editing.get(m?.id)
      if (!m) continue
      patch(row.querySelector('.where'), e ? typed({ ...e, name: e.name }).name : esc(m.name))
      patch(row.querySelector('.what'), esc(detail(m)) + (e?.field === 'phone' ? CARET : ''))
      const label = e ? 'typing…' : touching.has(m.id) ? `${touching.get(m.id)} tapped this` : ''
      patch(row.querySelector('.livechip'), esc(label))
      row.classList.toggle('live', !!label)
    }
    if (view && ready()) paintPhoto(ready())
  }

  const commit = () => {
    saveRoom(room)
    conn?.broadcast()
    render()
  }

  onMeSaved = (next) => {
    me = next
    if (joined && upsertMe(room, me)) commit()
  }

  function highlight(list) {
    for (const m of list) {
      ui.fresh.add(m.id)
      setTimeout(() => ui.fresh.delete(m.id), 2400)
    }
  }

  // Anyone in the room can add people who couldn't make it, or whose phone can't join: from the
  // contact picker where the browser has one, by typing a name and number, or by pasting a list.
  function openAdd(asList = remote) {
    if (asList) return openAddList()
    $('addBody').innerHTML = `
      ${canPickContacts ? `<button type="button" class="block suite-primary" id="pick">Choose from contacts</button>
        <p class="fine or">or type it in</p>` : ''}
      ${profileForm({ cta: 'Add', someoneElse: true })}
      <p class="fine or"><button type="button" class="linkbtn" id="addList">Paste several numbers instead</button></p>`
    $('addList').onclick = () => openAddList()
    const form = $('addBody').querySelector('form')
    wireProfile(form, (m) => {
      $('add').close()
      highlight([m])
      commit()
    }, (person) => addOther(room, person, me.id) ?? 'That number is already in the group.')
    liveForm(form, 'adding', $('add'))

    $('pick')?.addEventListener('click', async () => {
      const err = form.querySelector('.error')
      try {
        const picks = await navigator.contacts.select(['name', 'tel'], { multiple: true })
        const ok = [], skipped = []
        for (const c of picks) {
          const phone = (c.tel ?? []).map(normalize).find(Boolean)
          const name = c.name?.find((n) => n?.trim())?.trim() || (phone && pretty(phone))
          const m = phone && addOther(room, { name: name.slice(0, 60), phone }, me.id)
          if (m) ok.push(m)
          else if (!phone) skipped.push(c.name?.[0] || 'a contact')
        }
        if (ok.length) {
          highlight(ok)
          commit()
        }
        if (skipped.length) err.textContent = `No usable mobile number for ${joinNames(skipped)}.`
        else $('add').close()
      } catch (e) {
        if (e.name !== 'AbortError') err.textContent = "Couldn't open contacts. Type it in instead."
      }
    })
    if (!$('add').open) $('add').showModal()
    if (!canPickContacts) form.querySelector('input').focus()
  }

  function openAddList() {
    present({ adding: undefined })
    $('addBody').innerHTML = `<div class="add-list"></div><button type="button" class="block suite-primary" id="addMany" disabled>Add</button>
      <p class="fine or"><button type="button" class="linkbtn" id="addOne">Add one person instead</button></p>`
    let list = []
    pasteBox($('addBody').querySelector('.add-list'), {
      myPhone: me.phone,
      existing: new Set(people().map((m) => m.phone)),
      onChange(people) {
        list = people
        $('addMany').disabled = !list.length
        $('addMany').textContent = list.length ? `Add ${plural(list.length, 'person', 'people')}` : 'Add'
      },
    })
    $('addOne').onclick = () => openAdd(false)
    $('addMany').onclick = () => {
      const now = Date.now()
      const added = list.map((p, i) => {
        const m = addOther(room, { name: p.name || pretty(p.phone), phone: p.phone }, me.id)
        if (m) m.j = m.u = now + i
        return m
      }).filter(Boolean)
      $('add').close()
      highlight(added)
      commit()
    }
    if (!$('add').open) $('add').showModal()
  }

  async function act(el) {
    const a = el.dataset.act
    showError('')
    if (a === 'add') openAdd()
    else if (a === 'photo') pickPhoto()
    else if (a === 'resend-photo') addPhoto(null)
    else if (a === 'retry-photo') {
      loadingT = null
      picError = ''
      renderPhoto()
    }
    else if (a === 'help') {
      renderHelp()
      if (!$('help').open) $('help').showModal()
    } else if (a === 'copy-numbers') copyFrom(el, others().map((m) => m.phone).join('\n')) // iOS splits on newlines, not commas
    else if (a === 'copy-message') copyFrom(el, body())
    else if (a === 'unedit') {
      ui.editing = null
      renderPeople()
    } else if (a === 'remove') {
      removeMember(room, el.closest('[data-id]').dataset.id)
      ui.editing = null
      commit()
    } else if (a === 'save-contacts') openSave()
    else if (a === 'send-cards') sendCards(el)
    else if (a === 'vcf') {
      try {
        const list = await withPhotos(withFaces(others()), ready())
        await deliverVcf(buildVcf(list, { day: day() }), vcfFilename(day()), el.dataset.method)
      } catch (err) {
        if (err.name !== 'AbortError') showError(err.message)
      }
    }
  }

  // Saving from the room: the same one-at-a-time list as the link in the text.
  let saveSheet = null
  const saveList = () => ({ members: withFaces(people()), photo: ready() })
  function openSave() {
    saveSheet = saver($('saveBody'), { ...saveList(), myPhone: me.phone, day: day(), key: `room.${id}`, inline: true })
    $('save').showModal()
  }

  // Tapping your own row opens "You"; tapping someone else's offers to remove a bad entry.
  function tapRow(row) {
    const m = room.members[row.dataset.id]
    if (!m) return
    if (m.phone === me.phone) {
      openMe()
      return liveForm($('meBody').querySelector('form'), 'editing', $('me'))
    }
    ui.editing = ui.editing === m.id ? null : m.id
    present({ touching: ui.editing ?? undefined })
    renderPeople()
    renderLive()
  }

  // Taps in the room and in its "Didn't work?" sheet (a dialog outside #app) share one handler.
  const onTap = (e) => {
    if (e.target.closest('#photoBox .photo')) {
      const i = faceTapped(e)
      if (i == null) return
      refreshForm && !joined && setTimeout(refreshForm)
      return tapFace(i)
    }
    if (!joined) return
    keepAwake()
    // Starting the text: tell everyone right away. The link itself still opens Messages; the
    // re-render waits a tick so the tapped link stays in the page while the browser follows it.
    if (e.target.closest('[data-sms]')) {
      markSent(room, me.id)
      saveRoom(room)
      conn?.broadcast()
      syncLink(true)
      ifNothingOpened(() => helpHook?.(true))
      setTimeout(render, 50)
      return
    }
    const el = e.target.closest('[data-act]')
    if (el) return act(el)
    const row = e.target.closest('#people .card')
    if (row) tapRow(row)
  }
  app.onclick = onTap
  $('help').onclick = onTap
  app.onkeydown = (e) => {
    const row = e.target.closest?.('#people .card')
    if (row && e.target === row && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault()
      tapRow(row)
    }
  }

  onPhoto = (file) => {
    if (!me || !joined) return
    addPhoto(file)
  }

  // --- connection --------------------------------------------------------------------------
  let conn = null
  let left = false
  let gen = 0

  function start() {
    const g = ++gen
    connect({
      id,
      secret,
      getSnapshot: () => snapshot(room, me?.id),
      onSnapshot: (snap) => {
        const { changed, added } = merge(room, snap)
        if (!changed) return false
        if (joined) saveRoom(room)
        const news = added.filter((m) => m.phone !== me?.phone)
        if (joined && news.length) {
          highlight(news)
          navigator.vibrate?.(25)
        }
        render()
        return true
      },
      onPresence: (others) => {
        ui.live = others
        renderLive()
      },
      onChange: (status) => {
        ui.status = status
        if (!joined) return
        renderStatus()
        if ($('help').open) renderHelp()
      },
    }).then((c) => {
      if (left || g !== gen) return c.leave()
      conn = c
      present({})
    })
  }

  // A locked phone loses its connections. Coming back, give the normal reconnect a moment, then
  // start fresh if nobody has reappeared.
  const onVisibility = () => {
    if (document.visibilityState !== 'visible') return
    if (joined) keepAwake()
    setTimeout(() => {
      if (left || !conn || Object.keys(conn.status.peers).length || people().length < 2) return
      conn.leave()
      conn = null
      start()
    }, 2500)
  }
  document.addEventListener('visibilitychange', onVisibility)

  if (me) enter()
  else mountJoin()
  start()
  if (waitingPhoto) {
    onPhoto(waitingPhoto)
    waitingPhoto = null
  }

  helpHook = (didntOpen) => {
    renderHelp(didntOpen)
    if (!$('help').open) $('help').showModal()
  }

  teardown = () => {
    left = true
    onPhoto = null
    helpHook = null
    clearTimeout(linkTimer)
    $('help').onclick = null
    $('save').onclick = null
    conn?.leave()
    document.removeEventListener('visibilitychange', onVisibility)
    document.body.classList.remove('with-bar')
    wakeLock?.release().catch(() => {})
  }
}

// --- saving contacts, one at a time ------------------------------------------------------------
// iPhones don't reliably save several contacts from one file, so each person is their own card:
// tap a face or a name, or the big button, which walks through everyone in turn. Each card has the
// person's face from the group photo, so the contact shows who it is. Your own row is never saved.
// Who's been saved is remembered on this phone, in case opening a card reloads the page.

function saver(root, { members, photo, myPhone, day, key, inline = false, bar = null }) {
  const done = new Set(load(`saved.${key}`, []))
  const skipped = new Set()
  let list = members
  let view = null
  const savable = () => list.filter((m) => m.phone !== myPhone)
  const next = () => savable().find((m) => !done.has(m.phone) && !skipped.has(m.phone))

  function mount() {
    root.innerHTML = `
      <p class="muted">Tap each person to save them.</p>
      ${photo ? '<div class="saver-photo"></div>' : ''}
      <div class="list saver-list"></div>
      <p class="error saver-error" role="alert"></p>
      ${inline ? '<div class="saver-bar"></div>' : ''}`
    view = photo ? facePhoto(root.querySelector('.saver-photo'), photo) : null
    paint()
  }

  function paint() {
    const byFace = new Map(list.filter((m) => m.face != null).map((m) => [m.face, m]))
    view?.paint((i) => {
      const m = byFace.get(i)
      if (!m) return { cls: 'blank', label: 'Not in the group' }
      if (m.phone === myPhone) return { cls: 'mine', tag: 'You', label: 'You' }
      return done.has(m.phone)
        ? { cls: 'saved', tag: `${firstName(m.name)} ✓`, label: `${m.name}, saved. Tap to save again` }
        : { cls: 'taken', tag: firstName(m.name), label: `Save ${m.name}` }
    })
    root.querySelector('.saver-list').innerHTML = list.map((m, i) => {
      const who = `${avatar(photo, m.face)}<span class="who"><span class="where">${esc(m.name)}</span><span class="what">${esc(detail(m))}</span></span>`
      return m.phone === myPhone
        ? `<div class="card pick-me">${who}<span class="chip">you</span></div>`
        : `<button type="button" class="card pick" data-act="save-one" data-i="${i}">${who}<span class="chip">${done.has(m.phone) ? 'saved ✓' : skipped.has(m.phone) ? 'skipped' : 'save'}</span></button>`
    }).join('')
    const n = next()
    const left = savable().filter((m) => !done.has(m.phone)).length
    const barEl = inline ? root.querySelector('.saver-bar') : bar
    barEl.innerHTML = `
      <div class="bar-inner">${n
        ? `<button type="button" class="primary" data-act="save-next">Save ${esc(n.name)}</button>`
        : `<button type="button" class="primary" disabled>${left ? 'Tap anyone to save them' : 'Everyone’s saved ✓'}</button>`}</div>
      <p class="fine">${n ? `Already have ${esc(firstName(n.name))}? <button type="button" class="linkbtn" data-act="skip">Skip</button>` : ''}${
        n && !isApple && left > 1 ? ` · <button type="button" class="linkbtn" data-act="save-all">Save all ${left} at once</button>` : ''}</p>`
  }

  async function saveOne(m) {
    root.querySelector('.saver-error').textContent = ''
    done.add(m.phone)
    save(`saved.${key}`, [...done])
    paint()
    try {
      const [card] = await withPhotos([m], photo)
      await deliverVcf(buildVcf([card], { day }), cardFilename(m.name).replace(/\.vcf$/, ''))
    } catch (err) {
      if (err.name !== 'AbortError') root.querySelector('.saver-error').textContent = err.message
    }
  }

  async function saveAll() {
    const rest = savable().filter((m) => !done.has(m.phone))
    for (const m of rest) done.add(m.phone)
    save(`saved.${key}`, [...done])
    paint()
    try {
      await deliverVcf(buildVcf(await withPhotos(rest, photo), { day }), vcfFilename(day))
    } catch (err) {
      if (err.name !== 'AbortError') root.querySelector('.saver-error').textContent = err.message
    }
  }

  const onClick = (e) => {
    const el = e.target.closest('[data-act]')
    if (e.target.closest('.saver-photo .photo')) {
      const i = faceTapped(e)
      const m = i == null ? null : list.find((x) => x.face === i)
      if (m && m.phone !== myPhone) saveOne(m)
    } else if (el?.dataset.act === 'save-one') saveOne(list[Number(el.dataset.i)])
    else if (el?.dataset.act === 'save-next') next() && saveOne(next())
    else if (el?.dataset.act === 'skip') {
      const n = next()
      if (n) skipped.add(n.phone)
      paint()
    } else if (el?.dataset.act === 'save-all') saveAll()
  }
  root.onclick = onClick
  if (bar && bar !== root) bar.onclick = onClick

  mount()
  return {
    // The room keeps changing while the sheet is open.
    refresh({ members: m, photo: p }) {
      list = m
      if (p !== photo) {
        photo = p
        mount()
      } else paint()
    },
    saveAll,
  }
}

// The "save everyone" page: the link inside the group text.
async function showContacts(hash) {
  onMeSaved = route
  const failed = (title, text) => {
    app.innerHTML = `<section class="login"><h1>${esc(title)}</h1><p class="muted">${esc(text)}</p><a class="button block suite-primary" href="/">Start a group text</a></section>`
  }
  let roster
  if (isToken(hash)) {
    app.innerHTML = '<section class="login"><h1>Save everyone</h1><p class="muted"><span class="spinner"></span> Loading…</p></section>'
    try {
      roster = readPayload(await getJson('link', hash))
    } catch {
      return failed("Couldn't load this link", 'Check your connection and try again.')
    }
    if (!roster) return failed('This link has expired', 'Links work for 30 days. Ask someone in the group to start a new one.')
  } else {
    roster = decodeRoster(hash)
    if (!roster) return failed("This link didn't work", 'It looks incomplete. It may have been cut off when it was copied.')
  }
  const gen = ++contactsGen

  app.innerHTML = `
    <section class="login">
      <h1>Save everyone</h1>
      <div id="saverBox"></div>
      <details class="lab"><summary>didn't work? try another way</summary>
        <p class="fine">These save everyone at once.</p>
        <div class="lab-btns">
          ${Object.entries(VCF_METHODS).map(([k, label]) => `<button type="button" class="quiet" data-act="save" data-method="${k}">${esc(label)}</button>`).join('')}
          <button type="button" class="quiet" data-act="copy">Copy names and numbers</button>
        </div>
      </details>
    </section>
    <div class="actionbar" id="saverBar"></div>`
  document.body.classList.add('with-bar')
  teardown = () => document.body.classList.remove('with-bar')

  const opts = { members: roster.members, photo: null, myPhone: getMe()?.phone, day: roster.day, key: `link.${hash.slice(0, 40)}`, bar: $('saverBar') }
  const sheet = saver($('saverBox'), opts)
  if (roster.photo) {
    fetchPhoto(roster.photo)
      .then((p) => p && gen === contactsGen && sheet.refresh({ members: roster.members, photo: p }))
      .catch(() => {})
  }

  app.querySelector('.lab').onclick = async (e) => {
    const el = e.target.closest('[data-act]')
    if (el?.dataset.act === 'save') {
      showError('')
      try {
        const photo = roster.photo && (await fetchPhoto(roster.photo).catch(() => null))
        const list = roster.members.filter((m) => m.phone !== getMe()?.phone)
        await deliverVcf(buildVcf(await withPhotos(list, photo), { day: roster.day }), vcfFilename(roster.day), el.dataset.method)
      } catch (err) {
        if (err.name !== 'AbortError') app.querySelector('.saver-error').textContent = err.message
      }
    } else if (el?.dataset.act === 'copy') {
      copyFrom(el, roster.members.map((m) => `${m.name}: ${m.phone}${m.ig ? ` @${m.ig}` : ''}`).join('\n'))
    }
  }
}
let contactsGen = 0

// --- routing --------------------------------------------------------------------------------

function route() {
  teardown?.()
  teardown = null
  onMeSaved = null
  app.onclick = null
  app.onkeydown = null
  for (const d of document.querySelectorAll('dialog[open]')) d.close()
  const h = new URLSearchParams(location.hash.slice(1))
  if (h.has('g')) {
    // A link from split also carries names; keep them out of the address bar (and out of anything
    // copied from it) once read.
    const from = { name: h.get('n')?.trim().slice(0, 60) || undefined, table: h.getAll('t') }
    if (h.has('n') || h.has('t')) history.replaceState(null, '', `${location.pathname}${location.search}#g=${h.get('g')}`)
    showRoom(h.get('g'), from)
  }
  else if (location.pathname === '/c') showContacts(location.hash.slice(1))
  else if (h.has('c')) showContacts(h.get('c'))
  else if (h.has('paste')) showPaste()
  else showHome()
  window.scrollTo(0, 0)
}

window.addEventListener('hashchange', route)
route()
